import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { apiClient, formatSAR } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { Star, MapPin, Phone, Globe, MessageSquare, CheckCircle2, Send, Loader2 } from "lucide-react";
import VerifiedBadge from "../components/VerifiedBadge";

export default function PartnerDetailPage() {
  const { id } = useParams();
  const { lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ service_name: "", requested_date: "", notes: "", contact_name: user?.name || "", contact_phone: "" });
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    apiClient.get(`/partners/${id}`).then(({ data }) => setP(data));
  }, [id]);

  if (!p) return <div className="p-10 text-[#525252]"><Loader2 className="w-5 h-5 animate-spin" /></div>;

  const send = async (e) => {
    e.preventDefault();
    if (!user) { navigate("/login", { state: { from: `/services/partner/${id}` } }); return; }
    try {
      const { data } = await apiClient.post("/service-orders", { partner_id: id, ...form });
      setConfirmed(data);
      setShowForm(false);
    } catch (err) {
      alert(err.response?.data?.detail || "Error");
    }
  };

  if (confirmed) {
    return (
      <div className="max-w-2xl mx-auto p-12 text-center" data-testid="order-confirmed">
        <div className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-6"><CheckCircle2 className="w-12 h-12 text-green-600" /></div>
        <h1 className="font-display text-3xl mb-2">{lang === "ar" ? "تم إرسال طلبك" : "Order sent"}</h1>
        <p className="text-[#6b6b6b] mb-4">{lang === "ar" ? `رقم الطلب: ${confirmed.code}` : `Order #${confirmed.code}`}</p>
        <p className="text-[#3a3a3a] mb-8">{lang === "ar" ? "سيتم التواصل معك من قبل مزوّد الخدمة قريباً." : "The partner will contact you shortly."}</p>
        <button onClick={() => navigate("/orders")} className="btn-primary mx-auto" data-testid="goto-orders">{lang === "ar" ? "متابعة طلباتي" : "Track my orders"}</button>
      </div>
    );
  }

  return (
    <div className="max-w-[1100px] mx-auto px-5 py-10" data-testid="partner-detail">
      <div className="surface rounded-2xl overflow-hidden mb-6">
        <div className="h-48 bg-gradient-to-br from-[#c9a55c]/40 to-[#5a4615]/60 relative">
          {p.cover_image && <img src={p.cover_image} alt="" className="w-full h-full object-cover" />}
          {p.logo && <img src={p.logo} alt="" className="absolute bottom-4 start-4 w-24 h-24 rounded-2xl bg-white object-contain border-4 border-white shadow-xl" />}
        </div>
        <div className="p-6">
          <div className="flex justify-between items-start gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h1 className="font-display text-3xl">{lang === "ar" ? p.name_ar : (p.name_en || p.name_ar)}</h1>
                {p.verified && <VerifiedBadge status="approved" type="business" lang={lang} size="md" />}
              </div>
              <div className="text-sm text-[#6b6b6b] inline-flex items-center gap-3 flex-wrap mt-2">
                <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.city}{p.address && ` · ${p.address}`}</span>
                <span className="inline-flex items-center gap-1"><Star className="w-3 h-3 text-[#c9a55c] fill-current" /> {p.rating_avg?.toFixed(1) || "—"} ({p.rating_count || 0})</span>
              </div>
              <p className="text-sm text-[#3a3a3a] mt-3 max-w-2xl">{lang === "ar" ? p.description_ar : (p.description_en || p.description_ar)}</p>
            </div>
            <button onClick={() => setShowForm(true)} className="btn-primary" data-testid="request-service-btn"><Send className="w-4 h-4" /> {lang === "ar" ? "اطلب الخدمة" : "Request service"}</button>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-6">
          {p.services?.length > 0 && (
            <div className="surface rounded-2xl p-5">
              <h3 className="font-display text-xl mb-4">{lang === "ar" ? "الخدمات والأسعار" : "Services & pricing"}</h3>
              <div className="space-y-2">
                {p.services.map((s, i) => (
                  <div key={i} className="flex justify-between items-center p-3 rounded-lg border border-[#0a0a0a]/[0.06]">
                    <div><div className="font-medium">{s.name}</div>{s.description && <div className="text-xs text-[#6b6b6b]">{s.description}</div>}</div>
                    {s.price_sar > 0 && <div className="font-display text-lg text-[#8a6f2e]">{formatSAR(s.price_sar)} ريال</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="surface rounded-2xl p-5" data-testid="partner-reviews">
            <h3 className="font-display text-xl mb-4">{lang === "ar" ? "آراء العملاء" : "Customer reviews"} ({p.rating_count || 0})</h3>
            {(p.reviews || []).length === 0 ? (
              <p className="text-sm text-[#6b6b6b]">{lang === "ar" ? "لا توجد تقييمات بعد." : "No reviews yet."}</p>
            ) : (
              <div className="space-y-3">
                {p.reviews.map(r => (
                  <div key={r.id} className="p-3 rounded-lg border border-[#0a0a0a]/[0.06]">
                    <div className="flex justify-between items-center">
                      <div className="font-medium text-sm">{r.customer_name}</div>
                      <div className="flex">{Array.from({length:5}).map((_,i) => <Star key={i} className={`w-3 h-3 ${i < r.rating ? "text-[#c9a55c] fill-current" : "text-[#0a0a0a]/15"}`} />)}</div>
                    </div>
                    {r.comment && <p className="text-sm text-[#3a3a3a] mt-2">{r.comment}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <aside className="surface rounded-2xl p-5 h-fit lg:sticky lg:top-24 space-y-3 text-sm">
          <h4 className="font-display text-lg">{lang === "ar" ? "تواصل" : "Contact"}</h4>
          {p.phone && <div className="inline-flex items-center gap-2"><Phone className="w-4 h-4 text-[#8a6f2e]" /> <a href={`tel:${p.phone}`} className="text-[#0a0a0a] font-medium">{p.phone}</a></div>}
          {p.whatsapp && <div className="block"><a href={`https://wa.me/${p.whatsapp.replace(/[^0-9]/g,'')}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-green-700 font-medium"><MessageSquare className="w-4 h-4" /> WhatsApp</a></div>}
          {p.website && <div className="block"><a href={p.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[#8a6f2e]"><Globe className="w-4 h-4" /> {p.website.replace(/^https?:\/\//,'')}</a></div>}
          {p.hours && <div className="pt-3 border-t border-[#0a0a0a]/[0.06]"><div className="text-xs text-[#6b6b6b] mb-1">{lang === "ar" ? "ساعات العمل" : "Hours"}</div><div className="text-sm">{p.hours}</div></div>}
        </aside>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-[#0a0a0a]/60 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <form onSubmit={send} onClick={(e) => e.stopPropagation()} className="bg-[#f1ecdf] rounded-2xl max-w-md w-full p-6 space-y-3">
            <h3 className="font-display text-xl mb-2">{lang === "ar" ? "تفاصيل الطلب" : "Order details"}</h3>
            <input className="input-dark w-full" placeholder={lang === "ar" ? "نوع الخدمة" : "Service type"} value={form.service_name} onChange={(e) => setForm({ ...form, service_name: e.target.value })} data-testid="ord-service" />
            <input className="input-dark w-full" type="date" value={form.requested_date} onChange={(e) => setForm({ ...form, requested_date: e.target.value })} data-testid="ord-date" />
            <input className="input-dark w-full" placeholder={lang === "ar" ? "اسمك" : "Your name"} value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} required data-testid="ord-name" />
            <input className="input-dark w-full" placeholder={lang === "ar" ? "جوالك" : "Your phone"} value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} required data-testid="ord-phone" />
            <textarea className="input-dark w-full min-h-[80px]" placeholder={lang === "ar" ? "ملاحظات" : "Notes"} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="ord-notes" />
            <div className="flex gap-2 justify-end pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="btn-ghost">{lang === "ar" ? "إلغاء" : "Cancel"}</button>
              <button type="submit" className="btn-primary" data-testid="ord-submit">{lang === "ar" ? "إرسال الطلب" : "Send request"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
